import React, { useState, useEffect } from 'react';
import { useLocation, Redirect } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthState } from 'context';
import {
  generateOAuthURL,
  openAuthURL,
  isSubdomain,
  getOrganizationName,
  needsGDPRConsent,
  providers,
  appStage,
} from 'helpers';

import { CheckLabel, Toast } from 'components/shared';
import { useTranslation } from 'hooks';
import { setFlag, flags, getFlag } from 'services';
import { Logo } from 'components/shared/NavigationBar';

import {
  CallbackScreen,
  OAuthButton,
  SecureText,
  Footer,
  AuthLoader,
  LoginCheckbox,
} from 'screens/LoginScreen';

import styles from './LoginScreen.module.scss';
import { user } from 'services/analytics/events';

export const LoginScreen = () => {
  const location = useLocation();
  const isOAuthCallback = location.search.includes('access_code');
  const { isAuthenticated } = useAuthState();

  if (isAuthenticated) {
    return <Redirect to={'/'} />;
  }
  if (isOAuthCallback) {
    return <CallbackScreen />;
  }
  if (isSubdomain()) {
    return <OrganizationLogin />;
  }

  return <Login />;
};

const OrganizationLogin = () => {
  const [organizationName, setOrganizationName] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    getOrganizationName().then((response) => {
      setOrganizationName(response);
      setIsLoading(false);
    });
  }, []);

  if (isLoading) {
    return <AuthLoader />;
  } else if (organizationName === 'OrganizationNotFound') {
    return <Login />;
  } else {
    return <Login organization={organizationName} />;
  }
};

interface LoginProps {
  organization?: string;
}

const Login = ({ organization }: LoginProps) => {
  const { t } = useTranslation();
  const [needsGDPR, setNeedsGDPR] = useState(null);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [forceOnboarding, setForceOnboarding] = useState(getFlag(flags.ForceOnboarding) as boolean);

  useEffect(() => {
    needsGDPRConsent().then((response) => {
      //@ts-ignore
      setNeedsGDPR(response);
      setIsLoading(false);
    });
    user.signinView();
  }, []);
  const startLogin = (provider: any) => {
    openAuthURL(generateOAuthURL({ provider }), false, undefined, undefined, () => {
      toast(<Toast message={t('toasts.pop-up-blocked')} hasCloseButton />, {
        position: toast.POSITION.BOTTOM_LEFT,
        hideProgressBar: true,
        closeButton: false,
        closeOnClick: false,
        autoClose: false,
      });
    });
  };
  const checkedBox = (checked: boolean) => {
    setIsChecked(checked);
  };

  const handleForceOnboarding = () => {
    setForceOnboarding((currentForceOnboarding) => !currentForceOnboarding);
  };

  useEffect(() => {
    if (forceOnboarding) {
      setFlag(flags.ForceOnboarding, true);
    } else {
      setFlag(flags.ForceOnboarding, false);
    }
  }, [forceOnboarding]);

  return isLoading ? (
    <></>
  ) : (
    <div className={styles.container}>
      <div className={styles.Window}>
        <div className={styles.WindowContent}>
          {/* Inside window */}
          <div className={styles.header}>
              <Logo className={styles.niraLogo} isNira />  
            {organization ? (
              <div className={styles.copy}>
                <span>Sign in to access the</span>
                <span className={styles.organizationName}> {organization} </span>
                <span>organization</span>
              </div>
            ) : (
              <div className={styles.copy}>Sign in to connect your documents to Nira.</div>
            )}
            <div className={styles.buttonRow}>
              <OAuthButton
                type={providers.google.id}
                onClick={() => (needsGDPR && !isChecked ? null : startLogin(providers.google.id))}
                disabled={needsGDPR && !isChecked}
                tid="btn-login-google"
              />
              <OAuthButton
                type={providers.microsoft.id}
                onClick={() =>
                  needsGDPR && !isChecked ? null : startLogin(providers.microsoft.id)
                }
                disabled={needsGDPR && !isChecked}
                tid="btn-login-ms"
              />
            </div>
            {needsGDPR && (
              <LoginCheckbox
                isChecked={isChecked}
                onChange={(isChecked) => checkedBox(isChecked)}
              />
            )}
            {appStage !== 'prod' && (
              <CheckLabel
                className={styles.checkbox}
                checked={forceOnboarding}
                onChange={handleForceOnboarding}
                type="Flat"
              >
                Force Onboarding
              </CheckLabel>
            )}

            <SecureText className={styles.secureText} />
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};
